import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, CircleHelp, Menu, Search } from "lucide-react";
import { colors, header } from "../styles/index";
import { useProfile, isOwner } from "../context/ProfileContext";
import { useStudioTours } from "../reacttour/StudioTourContext";
import { getOverview, listPlans } from "../game-builder/api/gameBuilderAPI";

export const ROLE_LABEL = { admin: "Admin", owner: "Owner", creator: "Creator" };
export const displayName = (email) => {
  const name = (email || "").split("@")[0].replace(/[._-]+/g, " ").trim();
  return name ? name.replace(/\b\w/g, (c) => c.toUpperCase()) : "You";
};

// Find a simulation by its name or its plan's - the list loads the first time the box is used.
function SimulationSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [sims, setSims] = useState(null);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const load = () => {
    if (!sims) getOverview().then((d) => setSims(d.simulations)).catch(() => setSims([]));
  };
  const q = query.trim().toLowerCase();
  const found = q && sims ? sims.filter((s) => `${s.title} ${s.plan}`.toLowerCase().includes(q)).slice(0, 8) : [];
  const pick = (s) => { setOpen(false); setQuery(""); navigate(`/app/studio?plan=${encodeURIComponent(s.plan_id)}`); };

  return (
    <div className="as-search" ref={boxRef}>
      <Search size={18} aria-hidden="true" />
      <input type="search" placeholder="Search simulations..." value={query} aria-label="Search simulations"
        onFocus={() => { load(); setOpen(true); }} onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={(e) => { if (e.key === "Enter" && found[0]) pick(found[0]); if (e.key === "Escape") setOpen(false); }} />
      {open && q && (
        <div className="as-search-results" role="listbox">
          {!sims && <div className="as-search-empty">Loading...</div>}
          {sims && found.length === 0 && <div className="as-search-empty">No simulation matches &ldquo;{query}&rdquo;</div>}
          {found.map((s) => (
            <button key={`${s.plan_id}/${s.mission_id}`} type="button" role="option" aria-selected="false" onClick={() => pick(s)}>
              <strong>{s.title}</strong><span>{s.plan}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// The workspace's top bar: (on a phone) the menu button and the brand, the search, the walkthrough, and the avatar menu.
const Header = ({ logout, onMenu }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const { profile } = useProfile();
  const avatar = localStorage.getItem("avatarEmoji") || profile?.avatar || "👤";
  const { startTour } = useStudioTours();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const go = (fn) => () => { setMenuOpen(false); fn(); };
  const walkthrough = async () => {
    navigate("/app/dashboard");
    const hasPlans = await listPlans().then((d) => d.plans.length > 0).catch(() => false);
    startTour("welcome", { hasPlans, isDemo: !!profile?.org?.is_demo, isOwner: isOwner(profile) });
  };

  return (
    <header className="as-topbar">
      {onMenu && (
        <>
          <button type="button" id="mobile-menu-toggle" className="as-icon-btn" onClick={onMenu} aria-label="Open the menu">
            <Menu size={22} />
          </button>
          <button type="button" className="as-brand-link as-topbar-brand" onClick={() => navigate("/app")}>
            <img src="/logo.svg" alt="" /> <span>Simulate<b>Job</b></span>
          </button>
        </>
      )}
      {!onMenu && <SimulationSearch />}

      <div ref={menuRef} className="as-topbar-right">
        <button type="button" className="as-icon-btn" onClick={walkthrough} title="Show me around" aria-label="Show me around">
          <CircleHelp size={22} />
        </button>
        <button type="button" id="avatar-menu" className="as-profile" onClick={() => setMenuOpen((prev) => !prev)}
          title={profile?.email || "Your account"} aria-haspopup="menu" aria-expanded={menuOpen}>
          <span className="as-avatar" aria-hidden="true">{avatar}</span>
          {!onMenu && (
            <span className="as-profile-text">
              <strong>{displayName(profile?.email)}</strong>
              <span>{ROLE_LABEL[profile?.role] || "Creator"}{profile?.org?.org_name ? ` · ${profile.org.org_name}` : ""}</span>
            </span>
          )}
          <ChevronDown size={16} aria-hidden="true" />
        </button>

        {menuOpen && (
          <div className="as-menu" role="menu">
            {profile && <div className="as-menu-who">{profile.email}</div>}
            <MenuItem onClick={go(walkthrough)}>Start the walkthrough</MenuItem>
            <MenuItem onClick={go(() => navigate("/app/settings?section=walkthroughs"))}>All walkthroughs</MenuItem>
            <MenuItem onClick={go(() => navigate("/app/settings"))}>Settings</MenuItem>
            <div style={{ height: 1, background: colors.border, margin: "8px 0" }} />
            <MenuItem danger onClick={go(() => logout())}>Sign out</MenuItem>
          </div>
        )}
      </div>
    </header>
  );
};

const MenuItem = ({ children, onClick, danger }) => (
  <div
    role="menuitem"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => { if (e.key === "Enter") onClick(); }}
    style={{ ...header.menuItem, ...(danger ? header.menuItemDanger : {}) }}
    onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgSoft; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
  >
    {children}
  </div>
);

export default Header;
